import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  addOutline,
  chatbubbleEllipsesOutline,
  closeOutline,
  listOutline,
  micOutline,
  sendOutline,
  stop
} from "ionicons/icons";
import { useTranslation } from "react-i18next";

import {
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonSpinner
} from "@ionic/react";

import { aiChatActions } from "../../api/AIChatActions";
import AuthContext from "../../context/AuthContext";
import { useAudioRecording } from "../../hooks/useAudioRecording";
import { useWindowSize } from "../../hooks/useWindowSize";
import type { AIChatRequest } from "../../types/requestTypes";
import type {
  AIChatSessionHistory,
  AIChatStreamChunk,
  AIChatStreamComplete,
  AIChatStreamEvent,
  AIChatStreamMetadata
} from "../../types/responseTypes";

import styles from "./AIChatWidget.module.scss";

const CHAT_SESSION_STORAGE_KEY = "ai_chat_session_id";

type MessageRole = "user" | "assistant";

type ChatMessage = {
  id: string;
  role: MessageRole;
  content: string;
  isStreaming?: boolean;
  hasError?: boolean;
  metadata?: AIChatStreamMetadata;
};

const makeMessageId = (): string =>
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const isChunkEvent = (event: AIChatStreamEvent): event is AIChatStreamChunk =>
  typeof (event as AIChatStreamChunk).answer_chunk === "string";

const isCompleteEvent = (
  event: AIChatStreamEvent
): event is AIChatStreamComplete =>
  typeof (event as AIChatStreamComplete).answer_complete === "string";

const isMetadataEvent = (
  event: AIChatStreamEvent
): event is AIChatStreamMetadata =>
  !isChunkEvent(event) && !isCompleteEvent(event);

const parseInlineBold = (text: string): React.ReactNode => {
  const splitByBold = text.split(/(\*\*[^*]+\*\*)/g);
  return splitByBold.map((token, index) => {
    if (token.startsWith("**") && token.endsWith("**")) {
      return <strong key={`${token}-${index}`}>{token.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`${token}-${index}`}>{token}</React.Fragment>;
  });
};

const getSpeechRecognitionLanguage = (language: string | undefined): string =>
  language?.startsWith("ro") ? "ro-RO" : "en-US";

const mergeTranscriptIntoInput = (
  currentInput: string,
  transcript: string
): string => {
  const trimmedTranscript = transcript.trim();
  if (!trimmedTranscript) return currentInput;

  const trimmedInput = currentInput.trim();
  return trimmedInput
    ? `${trimmedInput} ${trimmedTranscript}`
    : trimmedTranscript;
};

export const AIChatWidget: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { layout } = useWindowSize();
  const { isLoggedIn, ragTokenCtx } = useContext(AuthContext);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [input, setInput] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [viewMode, setViewMode] = useState<"chat" | "sessions">("chat");
  const [isSpeechButtonHovered, setIsSpeechButtonHovered] =
    useState<boolean>(false);
  const [speechErrorKey, setSpeechErrorKey] = useState<string | null>(null);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(false);
  const [nextMessageId, setNextMessageId] = useState<string | null>(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(false);
  const [sessionsHistory, setSessionsHistory] = useState<
    AIChatSessionHistory[]
  >([]);
  const [isLoadingSessionsHistory, setIsLoadingSessionsHistory] =
    useState<boolean>(false);

  const contentRef = useRef<HTMLIonContentElement | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<string>("");
  // Holds the text that was in the input box when recording started, so that
  // incoming speech is appended to it rather than replacing it.
  const speechInputBaseRef = useRef<string>("");
  const isFirstAuthRenderRef = useRef<boolean>(true);
  const shouldScrollToBottomRef = useRef<boolean>(false);

  const {
    transcript: speechTranscript,
    listening,
    isMicrophoneAvailable,
    toggleMicrophone,
    stopRecording,
    browserSupportsSpeechRecognition
  } = useAudioRecording({
    language: getSpeechRecognitionLanguage(i18n.resolvedLanguage),
    sounds: false
  });

  const canSend = useMemo(
    () => input.trim().length > 0 && !isStreaming,
    [input, isStreaming]
  );

  const speechStatusKey = useMemo(() => {
    if (speechErrorKey) return speechErrorKey;
    if (listening) return "aiChat.speechListening";
    if (!browserSupportsSpeechRecognition) return "aiChat.speechUnsupported";
    return null;
  }, [listening, browserSupportsSpeechRecognition, speechErrorKey]);

  // Merge live speech transcript into the input field while recording.
  // speechInputBaseRef captures the text that existed before recording started
  // so that dictated text is appended rather than replacing manual typing.
  useEffect(() => {
    if (!speechTranscript || !listening) return;
    setInput(
      mergeTranscriptIntoInput(speechInputBaseRef.current, speechTranscript)
    );
  }, [speechTranscript, listening]);

  // Show a permission error if the user denies microphone access.
  useEffect(() => {
    if (!isMicrophoneAvailable) {
      setSpeechErrorKey("aiChat.speechPermissionDenied");
    }
  }, [isMicrophoneAvailable]);

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  const scrollToBottom = useCallback((duration = 300) => {
    contentRef.current?.scrollToBottom(duration);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (shouldScrollToBottomRef.current) {
      shouldScrollToBottomRef.current = false;
      scrollToBottom(300);
    }
  }, [messages, isOpen, scrollToBottom]);

  useEffect(
    () => () => {
      streamAbortRef.current?.abort();
      stopRecording(true);
    },
    [] // intentional empty deps: cleanup runs only on unmount
  );

  useEffect(() => {
    const storedSessionId = localStorage.getItem(CHAT_SESSION_STORAGE_KEY);
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);

  useEffect(() => {
    if (viewMode !== "chat") {
      stopRecording(true);
      setIsSpeechButtonHovered(false);
    }
  }, [viewMode]);

  useEffect(() => {
    if (isFirstAuthRenderRef.current) {
      isFirstAuthRenderRef.current = false;
      return;
    }

    streamAbortRef.current?.abort();
    stopRecording(true);
    setIsSpeechButtonHovered(false);
    setIsStreaming(false);
    setInput("");
    setMessages([]);
    setSessionId(null);
    setHasMoreMessages(false);
    setNextMessageId(null);
    setSessionsHistory([]);
    setViewMode("chat");
    localStorage.removeItem(CHAT_SESSION_STORAGE_KEY);
    setSpeechErrorKey(null);
  }, [isLoggedIn]);

  const loadSessionMessages = useCallback(
    async (targetSessionId: string, before?: string | null) => {
      if (!ragTokenCtx || isLoadingMessages) return;

      setIsLoadingMessages(true);
      try {
        const result = await aiChatActions.getSessionMessages(
          targetSessionId,
          ragTokenCtx,
          { before, limit: 20 }
        );

        const mapped = result.messages.map((m) => ({
          id: m.id_message,
          role: m.role,
          content: m.content
        }));

        if (!before) {
          setMessages(mapped);
          shouldScrollToBottomRef.current = true;
        } else {
          const scrollEl = await contentRef.current?.getScrollElement();
          const previousHeight = scrollEl?.scrollHeight ?? 0;

          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m.id));
            const unique = mapped.filter((m) => !existingIds.has(m.id));
            return [...unique, ...prev];
          });

          requestAnimationFrame(() => {
            void (async () => {
              const el = await contentRef.current?.getScrollElement();
              if (el) {
                el.scrollTop = el.scrollHeight - previousHeight;
              }
            })();
          });
        }

        setHasMoreMessages(result.has_more);
        setNextMessageId(result.next_message);
      } finally {
        setIsLoadingMessages(false);
      }
    },
    [ragTokenCtx, isLoadingMessages]
  );

  const handleLoadOlderMessages = useCallback(
    async (event: CustomEvent<void>) => {
      const infiniteScroll = event.target as HTMLIonInfiniteScrollElement;

      if (!hasMoreMessages || isLoadingMessages || !sessionId) {
        await infiniteScroll.complete();
        return;
      }

      await loadSessionMessages(sessionId, nextMessageId);
      await infiniteScroll.complete();
    },
    [
      hasMoreMessages,
      isLoadingMessages,
      sessionId,
      nextMessageId,
      loadSessionMessages
    ]
  );

  const loadSessionsHistory = useCallback(async () => {
    if (!ragTokenCtx) {
      setSessionsHistory([]);
      return;
    }

    setIsLoadingSessionsHistory(true);
    try {
      const response = await aiChatActions.getChatSessionsHistory(ragTokenCtx);
      setSessionsHistory(response);
    } finally {
      setIsLoadingSessionsHistory(false);
    }
  }, [ragTokenCtx]);

  const handleOpenSessionsHistory = async (): Promise<void> => {
    if (!isLoggedIn) return;
    setViewMode("sessions");
    await loadSessionsHistory();
  };

  const handleSelectSession = (selectedSessionId: string): void => {
    setSessionId(selectedSessionId);
    localStorage.setItem(CHAT_SESSION_STORAGE_KEY, selectedSessionId);
    setMessages([]);
    setHasMoreMessages(false);
    setNextMessageId(null);
    setViewMode("chat");
    void loadSessionMessages(selectedSessionId);
  };

  const handleStartNewSession = (): void => {
    streamAbortRef.current?.abort();
    stopRecording(true);
    setIsSpeechButtonHovered(false);
    setIsStreaming(false);
    setInput("");
    setMessages([]);
    setSessionId(null);
    setHasMoreMessages(false);
    setNextMessageId(null);
    localStorage.removeItem(CHAT_SESSION_STORAGE_KEY);
    setViewMode("chat");
    setSpeechErrorKey(null);
  };

  const handleCloseWidget = (): void => {
    stopRecording(true);
    setIsSpeechButtonHovered(false);
    setIsOpen(false);
  };

  const handleToggleSpeechRecognition = (): void => {
    if (!browserSupportsSpeechRecognition) return;

    if (listening) {
      stopRecording();
      setIsSpeechButtonHovered(false);
      return;
    }

    if (isStreaming) return;

    setSpeechErrorKey(null);
    // Capture whatever is already in the input so speech is appended to it.
    speechInputBaseRef.current = inputRef.current;
    toggleMicrophone();
  };

  const appendAssistantText = (messageId: string, chunk: string): void => {
    shouldScrollToBottomRef.current = true;
    setMessages((previousMessages) =>
      previousMessages.map((message) =>
        message.id === messageId
          ? { ...message, content: `${message.content}${chunk}` }
          : message
      )
    );
  };

  const finalizeAssistantMessage = (
    messageId: string,
    content?: string
  ): void => {
    setMessages((previousMessages) =>
      previousMessages.map((message) =>
        message.id === messageId
          ? {
              ...message,
              content: content ?? message.content,
              isStreaming: false
            }
          : message
      )
    );
  };

  const attachMetadata = (
    messageId: string,
    metadata: AIChatStreamMetadata
  ): void => {
    if (metadata.session_id) {
      setSessionId(metadata.session_id);
      localStorage.setItem(CHAT_SESSION_STORAGE_KEY, metadata.session_id);
    }

    setMessages((previousMessages) =>
      previousMessages.map((message) =>
        message.id === messageId ? { ...message, metadata } : message
      )
    );
  };

  const sendMessage = async (): Promise<void> => {
    stopRecording(true);

    const query = input.trim();
    if (!query || isStreaming) return;

    const assistantId = makeMessageId();
    const userId = makeMessageId();

    shouldScrollToBottomRef.current = true;
    setMessages((previousMessages) => [
      ...previousMessages,
      { id: userId, role: "user", content: query },
      { id: assistantId, role: "assistant", content: "", isStreaming: true }
    ]);
    setInput("");
    setIsOpen(true);
    setIsStreaming(true);

    streamAbortRef.current?.abort();
    streamAbortRef.current = new AbortController();

    let didReceiveCompletion = false;

    try {
      const request: AIChatRequest = {
        query,
        session_id: sessionId ?? undefined
      };
      await aiChatActions.sendMessageStream(
        request,
        {
          signal: streamAbortRef.current.signal,
          onEvent: (event) => {
            if (isChunkEvent(event)) {
              appendAssistantText(assistantId, event.answer_chunk);
              return;
            }

            if (isCompleteEvent(event)) {
              didReceiveCompletion = true;
              finalizeAssistantMessage(assistantId, event.answer_complete);
              return;
            }

            if (isMetadataEvent(event)) {
              attachMetadata(assistantId, event);
            }
          }
        },
        ragTokenCtx
      );

      if (!didReceiveCompletion) {
        finalizeAssistantMessage(assistantId);
      }
    } catch (error) {
      const isAbort =
        error instanceof Error && error.message === "Request aborted";

      if (!isAbort) {
        setMessages((previousMessages) =>
          previousMessages.map((message) =>
            message.id === assistantId
              ? {
                  ...message,
                  content: t("aiChat.error"),
                  hasError: true,
                  isStreaming: false
                }
              : message
          )
        );
      }
    } finally {
      setIsStreaming(false);
    }
  };

  return (
    <div className={`${styles.root} ${styles[`layout_${layout}`]}`}>
      {!isOpen && (
        <IonFab
          className={styles.chatFab}
          vertical="bottom"
          horizontal="end"
          slot="fixed"
        >
          <IonFabButton
            onClick={() => setIsOpen(true)}
            aria-label={t("aiChat.open")}
          >
            <IonIcon icon={chatbubbleEllipsesOutline} />
          </IonFabButton>
        </IonFab>
      )}

      {isOpen && (
        <section className={styles.panel} aria-label={t("aiChat.title")}>
          <header className={styles.panelHeader}>
            <h3 className={styles.title}>
              {viewMode === "sessions"
                ? t("aiChat.sessionsTitle", "Chat sessions")
                : t("aiChat.title")}
            </h3>
            <div className={styles.headerActions}>
              <button
                className={styles.headerIconButton}
                onClick={handleStartNewSession}
                aria-label={t("aiChat.newSession", "Start new chat session")}
              >
                <IonIcon icon={addOutline} />
              </button>
              {isLoggedIn && (
                <button
                  className={styles.headerIconButton}
                  onClick={() =>
                    viewMode === "chat"
                      ? void handleOpenSessionsHistory()
                      : setViewMode("chat")
                  }
                  aria-label={
                    viewMode === "chat"
                      ? t("aiChat.sessionsTitle", "Chat sessions")
                      : t("aiChat.title")
                  }
                >
                  <IonIcon icon={listOutline} />
                </button>
              )}
              <button
                className={styles.closeButton}
                onClick={handleCloseWidget}
                aria-label={t("aiChat.close")}
              >
                <IonIcon icon={closeOutline} />
              </button>
            </div>
          </header>

          {viewMode === "chat" && (
            <>
              <IonContent className={styles.messages} ref={contentRef}>
                <IonInfiniteScroll
                  position="top"
                  disabled={!hasMoreMessages}
                  onIonInfinite={handleLoadOlderMessages}
                  threshold="80px"
                >
                  <IonInfiniteScrollContent loadingSpinner="dots" />
                </IonInfiniteScroll>

                <div className={styles.messagesList}>
                  {isLoadingMessages && messages.length === 0 && (
                    <div className={styles.typingState}>
                      <IonSpinner name="dots" />
                    </div>
                  )}

                  {!isLoadingMessages && messages.length === 0 && (
                    <p className={styles.emptyState}>{t("aiChat.empty")}</p>
                  )}

                  {messages.map((message) => (
                    <article
                      key={message.id}
                      className={`${styles.messageRow} ${
                        message.role === "user" ? styles.user : styles.assistant
                      }`}
                    >
                      <div
                        className={`${styles.bubble} ${
                          message.hasError ? styles.error : ""
                        }`}
                      >
                        {message.content ? (
                          <p>{parseInlineBold(message.content)}</p>
                        ) : (
                          <div className={styles.typingState}>
                            <IonSpinner name="dots" />
                            <span>{t("aiChat.thinking")}</span>
                          </div>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </IonContent>

              <div className={styles.composerWrapper}>
                <div className={styles.composer}>
                  <input
                    className={styles.composerInput}
                    type="text"
                    value={input}
                    placeholder={t("aiChat.inputPlaceholder")}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void sendMessage();
                      }
                    }}
                  />
                  <button
                    className={`${styles.composerSpeech} ${
                      listening ? styles.composerSpeechActive : ""
                    }`}
                    onClick={handleToggleSpeechRecognition}
                    onMouseEnter={() => setIsSpeechButtonHovered(true)}
                    onMouseLeave={() => setIsSpeechButtonHovered(false)}
                    disabled={!browserSupportsSpeechRecognition || isStreaming}
                    aria-label={t(
                      browserSupportsSpeechRecognition
                        ? listening
                          ? "aiChat.speechStop"
                          : "aiChat.speechStart"
                        : "aiChat.speechUnsupported"
                    )}
                    title={t(
                      browserSupportsSpeechRecognition
                        ? listening
                          ? "aiChat.speechStop"
                          : "aiChat.speechStart"
                        : "aiChat.speechUnsupported"
                    )}
                  >
                    <IonIcon
                      icon={
                        listening && isSpeechButtonHovered ? stop : micOutline
                      }
                    />
                  </button>
                  <button
                    className={styles.composerSend}
                    onClick={() => void sendMessage()}
                    disabled={!canSend}
                    aria-label={t("aiChat.send")}
                  >
                    <IonIcon icon={sendOutline} />
                  </button>
                </div>
                {speechStatusKey && (
                  <p className={styles.composerStatus}>{t(speechStatusKey)}</p>
                )}
              </div>
            </>
          )}

          {viewMode === "sessions" && (
            <IonContent className={styles.messages}>
              <div className={styles.sessionList}>
                {isLoadingSessionsHistory && (
                  <div className={styles.sessionsLoading}>
                    <IonSpinner name="dots" />
                  </div>
                )}

                {!isLoadingSessionsHistory && sessionsHistory.length === 0 && (
                  <p className={styles.emptyState}>
                    {t("aiChat.noSessions", "No previous chat sessions found.")}
                  </p>
                )}

                {sessionsHistory.map((session) => (
                  <button
                    key={session.sessionId}
                    className={styles.sessionCard}
                    onClick={() => handleSelectSession(session.sessionId)}
                  >
                    <span className={styles.sessionTitle}>
                      {session.title ??
                        t("aiChat.untitledSession", "Untitled session")}
                    </span>
                    <span className={styles.sessionMeta}>
                      {new Date(session.updatedAt).toLocaleString()}
                    </span>
                  </button>
                ))}
              </div>
            </IonContent>
          )}
        </section>
      )}
    </div>
  );
};

export default AIChatWidget;
